import type { jsPDF } from 'jspdf'
import type { LEDProcessor, DataPage, WorkspaceElement } from '@/types/synoptique'
import type {
  MireInfo, ProcessorDetail, PanelSummary, CabinetSummary,
  CablesData, ConnectionsData, TranslateFn,
} from './report-types'
import {
  PAGE_W, MARGIN_L, MARGIN_R, CONTENT_W, LH, SEC_GAP,
  ACCENT, ACCENT_BG, GRAY_100, GRAY_200, GRAY_500, GRAY_700, GRAY_900, GREEN, ORANGE, RED,
  fmtNum, pageBreak, newTechnicalPage, drawPageHeader,
  sectionTitle, subSectionTitle, tableHeader, tableRow, totalRow,
  resetRowIndex, drawMetricBox, drawCard, drawBadge,
} from './report-helpers'

export interface TechnicalPageOpts {
  doc: jsPDF
  t: TranslateFn
  projectName: string
  mires: MireInfo[]
  processors: ProcessorDetail[]
  panels: PanelSummary[]
  cabinets: CabinetSummary[]
  cables: CablesData
  conns: ConnectionsData
  allElements: WorkspaceElement[]
  totalPanels: number
  totalProcessors: number
  grandTotalPixels: number
  procConsumption: number
  panelConsumption: number
  totalConsumption: number
  backupProcessors: Record<string, string>
  backupPorts: Record<string, Record<number, number>>
  dataPages: DataPage[]
}

// ═══════════════════════════════════════════════════════════════════════
// Main entry: draws all technical pages (each category = new page)
// ═══════════════════════════════════════════════════════════════════════
export function drawTechnicalPages(opts: TechnicalPageOpts): void {
  // Page 1: LED Walls Summary (specs + data connections per wall)
  if (opts.mires.length > 0) {
    drawLedWallsSummaryPage(opts)
  }

  // Page 2: Processors + Backup
  if (opts.processors.length > 0) {
    drawProcessorsPage(opts)
  }

  // Page 3: LED Panels (enriched specs)
  if (opts.panels.length > 0) {
    drawPanelsPage(opts)
  }

  // Page 4: Cabling & Connections
  drawCablingPage(opts)

  // Page 5: Power Summary
  drawPowerPage(opts)
}

// ═══════════════════════════════════════════════════════════════════════
// HELPER: Build mire → processor → port → panel count map (deduped)
// ═══════════════════════════════════════════════════════════════════════
function buildMireConnMap(
  dataPages: DataPage[],
  mires: MireInfo[],
): Map<string, Map<string, Map<number, number>>> {
  const elToMire = new Map<string, MireInfo>()
  for (const page of dataPages) {
    for (const el of page.workspaceElements) {
      if (el.type !== 'image') continue
      const d = el.data as Record<string, unknown>
      if (!d?.mireConfig) continue
      const cfg = d.mireConfig as { ledWallMode: { columns: number; rows: number; panelWidth: number; panelHeight: number } }
      const lw = cfg.ledWallMode
      const linked = d.linkedPanel as { resolution: { width: number; height: number } } | null
      const pw = linked?.resolution?.width ?? lw.panelWidth
      const ph = linked?.resolution?.height ?? lw.panelHeight
      const rawName = (d.name as string) ?? ''
      const shortName = rawName.split(' - ')[0].trim()
      const info = mires.find(m => m.name === shortName && m.totalPixels.width === pw * lw.columns && m.totalPixels.height === ph * lw.rows)
      if (info) elToMire.set(el.id, info)
    }
  }
  const result = new Map<string, Map<string, Map<number, number>>>()
  for (const mire of mires) result.set(mire.id, new Map())
  for (const page of dataPages) {
    for (const conn of (page.mireConnections ?? [])) {
      const info = elToMire.get(conn.imageId)
      if (!info) continue
      const procMap = result.get(info.id)!
      if (!procMap.has(conn.processorId)) procMap.set(conn.processorId, new Map())
      const portMap = procMap.get(conn.processorId)!
      portMap.set(conn.port, (portMap.get(conn.port) ?? 0) + 1)
    }
  }
  return result
}

// ═══════════════════════════════════════════════════════════════════════
// PAGE: LED WALLS SUMMARY (specs table + data connections per wall)
// ═══════════════════════════════════════════════════════════════════════
function drawLedWallsSummaryPage(opts: TechnicalPageOpts): void {
  const { doc, t, projectName, mires, grandTotalPixels, dataPages, backupPorts } = opts

  newTechnicalPage(doc)
  let y = drawPageHeader(doc, t('report.ledWallsSummary'), projectName)

  // Metric boxes
  const totalPanelCount = mires.reduce((s, m) => s + m.totalPanels, 0)
  const mBoxW = (CONTENT_W - 10) / 3
  drawMetricBox(doc, MARGIN_L, y, mBoxW, 22, t('report.ledWalls'), String(mires.length), true)
  drawMetricBox(doc, MARGIN_L + mBoxW + 5, y, mBoxW, 22, t('report.totalPanels'), fmtNum(totalPanelCount), false)
  drawMetricBox(doc, MARGIN_L + 2 * (mBoxW + 5), y, mBoxW, 22, t('report.totalPixels'), fmtNum(grandTotalPixels) + ' px', false)
  y += 30

  // All-walls summary table
  y = sectionTitle(doc, t('report.wallsAtAGlance'), y)
  resetRowIndex()
  const tCols = [
    { label: t('report.ledWall'), x: MARGIN_L },
    { label: t('report.grid'), x: MARGIN_L + 38, align: 'center' as const },
    { label: t('report.resolution'), x: MARGIN_L + 62 },
    { label: t('report.panels'), x: MARGIN_L + 102, align: 'center' as const },
    { label: t('report.panelType'), x: MARGIN_L + 118 },
    { label: t('report.pitch'), x: PAGE_W - MARGIN_R - 22, align: 'right' as const },
    { label: t('report.powerWall'), x: PAGE_W - MARGIN_R, align: 'right' as const },
  ]
  y = tableHeader(doc, tCols, y)

  for (const m of mires) {
    const wallPower = m.linkedPanel ? m.linkedPanel.powerConsumption * m.totalPanels : 0
    const panelLabel = m.linkedPanel ? `${m.linkedPanel.brand} ${m.linkedPanel.name}` : '-'
    const panelShort = panelLabel.length > 15 ? panelLabel.slice(0, 14) + '…' : panelLabel
    y = tableRow(doc, [
      { text: m.name, x: MARGIN_L, bold: true },
      { text: `${m.columns}×${m.rows}`, x: MARGIN_L + 38, align: 'center' },
      { text: `${m.totalPixels.width}×${m.totalPixels.height}`, x: MARGIN_L + 62 },
      { text: String(m.totalPanels), x: MARGIN_L + 102, align: 'center' },
      { text: panelShort, x: MARGIN_L + 118 },
      { text: m.linkedPanel ? `${m.linkedPanel.pixelPitch}mm` : '-', x: PAGE_W - MARGIN_R - 22, align: 'right' },
      { text: wallPower > 0 ? `${fmtNum(wallPower)} W` : '-', x: PAGE_W - MARGIN_R, align: 'right' },
    ], y)
  }
  const totalWallPower = mires.filter(m => m.linkedPanel).reduce((s, m) => s + m.linkedPanel!.powerConsumption * m.totalPanels, 0)
  y = totalRow(doc, y, [
    { text: t('report.total'), x: MARGIN_L },
    { text: String(totalPanelCount), x: MARGIN_L + 102, align: 'center' },
    { text: totalWallPower > 0 ? `${fmtNum(totalWallPower)} W` : '-', x: PAGE_W - MARGIN_R, align: 'right' },
  ])

  // Per-wall detail cards (brightness, IP, physical size, LED type…)
  const miresWithPanel = mires.filter(m => m.linkedPanel)
  if (miresWithPanel.length > 0) {
    y = sectionTitle(doc, t('report.wallSpecs'), y)
    const cardW = (CONTENT_W - 5) / 2
    for (let i = 0; i < miresWithPanel.length; i++) {
      const m = miresWithPanel[i]
      const col = i % 2
      const p = m.linkedPanel!
      const physW = p.dimensions?.width > 0 ? (p.dimensions.width * m.columns) / 1000 : 0
      const physH = p.dimensions?.height > 0 ? (p.dimensions.height * m.rows) / 1000 : 0
      const items: [string, string][] = [
        [t('report.grid'), `${m.columns} × ${m.rows}`],
        [t('report.resolution'), `${m.totalPixels.width} × ${m.totalPixels.height} px`],
        [t('report.panels'), String(m.totalPanels)],
      ]
      if (physW > 0 && physH > 0) items.push([t('report.physicalSize'), `${physW.toFixed(2)} × ${physH.toFixed(2)} m`])
      items.push([t('report.panelType'), `${p.brand} ${p.name}`])
      items.push([t('report.pitch'), `${p.pixelPitch} mm`])
      if (p.brightness > 0) items.push([t('report.brightness'), `${fmtNum(p.brightness)} nits`])
      if (p.ipRating && p.ipRating !== '-') items.push([t('report.ipRating'), p.ipRating])
      if (p.ledType) items.push([t('report.ledType'), p.ledType])
      if (p.powerConsumption > 0) items.push([t('report.powerWall'), `${fmtNum(p.powerConsumption * m.totalPanels)} W`])
      const cardH = 12 + items.length * 5 + 2
      const x = col === 0 ? MARGIN_L : MARGIN_L + cardW + 5
      if (col === 0) y = pageBreak(doc, y, cardH + 3)
      drawCard(doc, x, y, cardW, cardH, m.name, items)
      if (col === 1 || i === miresWithPanel.length - 1) y += cardH + 3
    }
  }

  // Data connections per LED wall
  const mireConnMap = buildMireConnMap(dataPages, mires)
  const hasConns = Array.from(mireConnMap.values()).some(m => m.size > 0)
  if (!hasConns) return

  y = pageBreak(doc, y, 20)
  y = sectionTitle(doc, t('report.dataConnections'), y)

  for (const mire of mires) {
    const procMap = mireConnMap.get(mire.id)
    if (!procMap || procMap.size === 0) continue

    y = pageBreak(doc, y, 26)
    // Wall header band
    doc.setFillColor(ACCENT_BG.r, ACCENT_BG.g, ACCENT_BG.b)
    doc.rect(MARGIN_L, y - 1, CONTENT_W, 9, 'F')
    doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
    doc.rect(MARGIN_L, y - 1, 3, 9, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
    doc.text(mire.name, MARGIN_L + 7, y + 5)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
    doc.text(
      `${mire.columns}×${mire.rows}  •  ${mire.totalPixels.width}×${mire.totalPixels.height} px  •  ${mire.totalPanels} panels`,
      MARGIN_L + 55, y + 5,
    )
    y += 13

    resetRowIndex()
    const cCols = [
      { label: t('report.processor'), x: MARGIN_L },
      { label: t('report.port'), x: MARGIN_L + 75 },
      { label: t('report.panelsShort'), x: MARGIN_L + 95, align: 'center' as const },
      { label: t('report.redundancy'), x: PAGE_W - MARGIN_R, align: 'right' as const },
    ]
    y = tableHeader(doc, cCols, y)

    for (const [procId, portMap] of procMap) {
      let proc: LEDProcessor | undefined
      for (const page of dataPages) {
        const el = page.workspaceElements.find(e => e.id === procId)
        if (el) { proc = el.data as LEDProcessor; break }
      }
      const procLabel = proc ? (proc.customName || `${proc.brand} ${proc.model}`) : procId
      const sortedPorts = Array.from(portMap.entries()).sort((a, b) => a[0] - b[0])
      let firstRow = true
      for (const [port, count] of sortedPorts) {
        const redundancy = backupPorts[procId]?.[port]
        y = tableRow(doc, [
          { text: firstRow ? procLabel : '', x: MARGIN_L, bold: firstRow },
          { text: `P${port}`, x: MARGIN_L + 75, bold: true },
          { text: `${count}`, x: MARGIN_L + 95, align: 'center' },
          { text: '', x: PAGE_W - MARGIN_R },
        ], y)
        if (redundancy !== undefined) {
          const badgeTxt = `R > P${redundancy}`
          drawBadge(doc, PAGE_W - MARGIN_R - doc.getTextWidth(badgeTxt) - 5, y - LH + 0.5, badgeTxt, ACCENT)
        }
        firstRow = false
      }
    }
    y += 4
  }
}

// ═══════════════════════════════════════════════════════════════════════
// PAGE: PROCESSORS
// ═══════════════════════════════════════════════════════════════════════
function drawProcessorsPage(opts: TechnicalPageOpts): void {
  const { doc, t, projectName, processors, allElements, backupProcessors, backupPorts } = opts

  newTechnicalPage(doc)
  let y = drawPageHeader(doc, t('report.processors'), projectName)

  // Metrics
  const mainCount = processors.filter(p => !p.isBackup).length
  const backupCount = processors.filter(p => p.isBackup).length
  const totalPorts = processors.reduce((s, p) => s + (typeof p.outputPorts === 'number' ? p.outputPorts : 0), 0)
  const mBoxW = (CONTENT_W - 10) / 3

  drawMetricBox(doc, MARGIN_L, y, mBoxW, 22, t('report.mainProcessors'), String(mainCount), true)
  drawMetricBox(doc, MARGIN_L + mBoxW + 5, y, mBoxW, 22, t('report.backupProcessors'), String(backupCount), false)
  drawMetricBox(doc, MARGIN_L + 2 * (mBoxW + 5), y, mBoxW, 22, t('report.totalOutputPorts'), String(totalPorts), false)
  y += 30

  // Processors table
  y = sectionTitle(doc, t('report.processorList'), y)
  resetRowIndex()
  const pCols = [
    { label: t('report.device'), x: MARGIN_L },
    { label: t('report.role'), x: MARGIN_L + 58 },
    { label: t('report.ports'), x: MARGIN_L + 110, align: 'center' as const },
    { label: t('report.powerUsage'), x: PAGE_W - MARGIN_R, align: 'right' as const },
  ]
  y = tableHeader(doc, pCols, y)

  let totalProcPower = 0
  for (const proc of processors) {
    const pw = typeof proc.powerConsumption === 'number' ? proc.powerConsumption : 0
    totalProcPower += pw
    const roleText = proc.isBackup ? `Backup` : 'Main'
    y = tableRow(doc, [
      { text: proc.customName || `${proc.brand} ${proc.model}`, x: MARGIN_L, bold: true },
      { text: roleText, x: MARGIN_L + 58 },
      { text: String(proc.outputPorts), x: MARGIN_L + 110, align: 'center' },
      { text: pw > 0 ? `${fmtNum(pw)} W` : '-', x: PAGE_W - MARGIN_R, align: 'right' },
    ], y)

    // Badge for role
    if (proc.isBackup) {
      drawBadge(doc, MARGIN_L + 58 + doc.getTextWidth('Backup') + 3, y - LH + 0.5, proc.backupOfLabel ? `de ${proc.backupOfLabel}` : '', ORANGE)
    } else {
      drawBadge(doc, MARGIN_L + 58 + doc.getTextWidth('Main') + 3, y - LH + 0.5, 'PRIMARY', GREEN)
    }
  }
  y = totalRow(doc, y, [
    { text: `${t('report.total')} (${processors.length})`, x: MARGIN_L },
    { text: `${fmtNum(totalProcPower)} W`, x: PAGE_W - MARGIN_R, align: 'right' },
  ])

  // Backup Configuration
  const hasBackups = Object.keys(backupProcessors).length > 0 || Object.keys(backupPorts).length > 0
  if (hasBackups) {
    y = sectionTitle(doc, t('report.backupConfig'), y)

    if (Object.keys(backupProcessors).length > 0) {
      resetRowIndex()
      const bCols = [
        { label: t('report.mainProcessor'), x: MARGIN_L },
        { label: '', x: MARGIN_L + 80 },
        { label: t('report.backupProcessor'), x: MARGIN_L + 90 },
      ]
      y = tableHeader(doc, bCols, y)
      for (const [mainId, backupId] of Object.entries(backupProcessors)) {
        const mainEl = allElements.find(e => e.id === mainId)
        const backupEl = allElements.find(e => e.id === backupId)
        const mainP = mainEl?.data as LEDProcessor | undefined
        const backupP = backupEl?.data as LEDProcessor | undefined
        y = tableRow(doc, [
          { text: mainP ? `${mainP.brand} ${mainP.model}` : mainId, x: MARGIN_L, bold: true },
          { text: '-->', x: MARGIN_L + 80 },
          { text: backupP ? `${backupP.brand} ${backupP.model}` : backupId, x: MARGIN_L + 90 },
        ], y)
      }
      y += 4
    }

    if (Object.keys(backupPorts).length > 0) {
      y = subSectionTitle(doc, t('report.portRedundancies'), y)
      resetRowIndex()
      const rCols = [
        { label: t('report.device'), x: MARGIN_L },
        { label: t('report.mainPort'), x: MARGIN_L + 80 },
        { label: t('report.redundancyPort'), x: MARGIN_L + 130 },
      ]
      y = tableHeader(doc, rCols, y)
      for (const [processorId, portMap] of Object.entries(backupPorts)) {
        const el = allElements.find(e => e.id === processorId)
        const proc = el?.data as LEDProcessor | undefined
        const label = proc ? (proc.customName || `${proc.brand} ${proc.model}`) : processorId
        for (const [mainPort, backupPort] of Object.entries(portMap)) {
          y = tableRow(doc, [
            { text: label, x: MARGIN_L },
            { text: `Port ${mainPort}`, x: MARGIN_L + 80 },
            { text: `Port ${backupPort}`, x: MARGIN_L + 130 },
          ], y)
        }
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
// PAGE: LED PANELS (enriched specs from database)
// ═══════════════════════════════════════════════════════════════════════
function drawPanelsPage(opts: TechnicalPageOpts): void {
  const { doc, t, projectName, panels, mires } = opts

  newTechnicalPage(doc)
  let y = drawPageHeader(doc, t('report.ledPanels'), projectName)

  // Panels table
  y = sectionTitle(doc, t('report.panelInventory'), y)
  resetRowIndex()
  const plCols = [
    { label: t('report.device'), x: MARGIN_L },
    { label: t('report.pitch'), x: MARGIN_L + 48 },
    { label: t('report.resolution'), x: MARGIN_L + 68 },
    { label: t('report.dimensions'), x: MARGIN_L + 96 },
    { label: t('report.quantity'), x: MARGIN_L + 135, align: 'center' as const },
    { label: t('report.powerUsage'), x: PAGE_W - MARGIN_R, align: 'right' as const },
  ]
  y = tableHeader(doc, plCols, y)

  let totalPanelPower = 0
  let totalPanelCount = 0
  for (const p of panels) {
    totalPanelPower += p.powerConsumption * p.count
    totalPanelCount += p.count
    y = tableRow(doc, [
      { text: `${p.brand} ${p.name}`, x: MARGIN_L, bold: true },
      { text: `${p.pixelPitch}mm`, x: MARGIN_L + 48 },
      { text: `${p.resolution.width} x ${p.resolution.height}`, x: MARGIN_L + 68 },
      { text: `${p.dimensions.width} x ${p.dimensions.height} mm`, x: MARGIN_L + 96 },
      { text: String(p.count), x: MARGIN_L + 135, align: 'center' },
      { text: `${fmtNum(p.powerConsumption * p.count)} W`, x: PAGE_W - MARGIN_R, align: 'right' },
    ], y)
  }
  y = totalRow(doc, y, [
    { text: t('report.total'), x: MARGIN_L },
    { text: String(totalPanelCount), x: MARGIN_L + 135, align: 'center' },
    { text: `${fmtNum(totalPanelPower)} W`, x: PAGE_W - MARGIN_R, align: 'right' },
  ])

  // Enriched panel detail cards (from DB data on linked panels)
  const linkedPanels = mires.filter(m => m.linkedPanel).map(m => m.linkedPanel!)
  const uniquePanels = new Map<string, typeof linkedPanels[0]>()
  for (const p of linkedPanels) {
    const key = `${p.brand}-${p.name}-${p.pixelPitch}`
    if (!uniquePanels.has(key)) uniquePanels.set(key, p)
  }

  if (uniquePanels.size > 0) {
    y += 4
    y = sectionTitle(doc, t('report.panelSpecs'), y)
    const cardW = (CONTENT_W - 5) / 2
    let cardIdx = 0

    for (const [, panel] of uniquePanels) {
      const col = cardIdx % 2
      const items: [string, string][] = [
        [t('report.pitch'), `${panel.pixelPitch}mm`],
        [t('report.resolution'), `${panel.resolution.width} x ${panel.resolution.height}`],
        [t('report.dimensions'), `${panel.dimensions.width} x ${panel.dimensions.height} mm`],
        [t('report.powerUsage'), `${fmtNum(panel.powerConsumption)} W`],
      ]
      if (panel.brightness > 0) items.push([t('report.brightness'), `${fmtNum(panel.brightness)} nits`])
      if (panel.ipRating && panel.ipRating !== '-') items.push([t('report.ipRating'), panel.ipRating])
      if (panel.panelWeight) items.push([t('report.weight'), panel.panelWeight])
      if (panel.ledType) items.push([t('report.ledType'), panel.ledType])
      if (panel.viewingAngle) items.push([t('report.viewingAngle'), panel.viewingAngle])
      if (panel.grayScale) items.push([t('report.grayScale'), panel.grayScale])
      if (panel.contrastRatio) items.push([t('report.contrastRatio'), panel.contrastRatio])
      if (panel.ledRefreshRate) items.push([t('report.refreshRate'), panel.ledRefreshRate])
      if (panel.receivingCard) items.push([t('report.receivingCard'), panel.receivingCard])
      if (panel.lineVoltage) items.push([t('report.lineVoltage'), panel.lineVoltage])

      const cardH = 12 + items.length * 5 + 2
      const x = col === 0 ? MARGIN_L : MARGIN_L + cardW + 5

      if (col === 0) y = pageBreak(doc, y, cardH + 3)
      drawCard(doc, x, y, cardW, cardH, `${panel.brand} ${panel.name}`, items)

      if (col === 1 || cardIdx === uniquePanels.size - 1) y += cardH + 3
      cardIdx++
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
// PAGE: CABLING & CONNECTIONS
// ═══════════════════════════════════════════════════════════════════════
function drawCablingPage(opts: TechnicalPageOpts): void {
  const { doc, t, projectName, cables, conns, cabinets, mires, dataPages, backupPorts } = opts

  newTechnicalPage(doc)
  let y = drawPageHeader(doc, t('report.cablingConnections'), projectName)

  // Metrics row
  const mBoxW = (CONTENT_W - 15) / 4
  drawMetricBox(doc, MARGIN_L, y, mBoxW, 22, t('report.dataCables'), String(cables.dataCount), true)
  drawMetricBox(doc, MARGIN_L + mBoxW + 5, y, mBoxW, 22, t('report.powerCables'), String(cables.powerCount), false)
  drawMetricBox(doc, MARGIN_L + 2 * (mBoxW + 5), y, mBoxW, 22, t('report.dataPortsUsed'), String(conns.usedDataPorts), false)
  drawMetricBox(doc, MARGIN_L + 3 * (mBoxW + 5), y, mBoxW, 22, t('report.powerOutletsUsed'), String(conns.usedPowerOutlets), false)
  y += 30

  // Cables table
  y = sectionTitle(doc, t('report.cablesSummary'), y)
  resetRowIndex()
  const cblCols = [
    { label: t('report.cableType'), x: MARGIN_L },
    { label: t('report.quantity'), x: PAGE_W - MARGIN_R, align: 'right' as const },
  ]
  y = tableHeader(doc, cblCols, y)

  if (cables.dataCount > 0) {
    y = tableRow(doc, [
      { text: t('report.dataCablesEthernet'), x: MARGIN_L },
      { text: String(cables.dataCount), x: PAGE_W - MARGIN_R, align: 'right' },
    ], y)
  }
  if (cables.powerCount > 0) {
    y = tableRow(doc, [
      { text: t('report.powerCablesElectric'), x: MARGIN_L },
      { text: String(cables.powerCount), x: PAGE_W - MARGIN_R, align: 'right' },
    ], y)
  }
  y = tableRow(doc, [
    { text: t('report.firstDataLinks'), x: MARGIN_L },
    { text: String(conns.firstData), x: PAGE_W - MARGIN_R, align: 'right' },
  ], y)
  y = tableRow(doc, [
    { text: t('report.firstPowerLinks'), x: MARGIN_L },
    { text: String(conns.firstPower), x: PAGE_W - MARGIN_R, align: 'right' },
  ], y)
  y = totalRow(doc, y, [
    { text: t('report.total'), x: MARGIN_L },
    { text: String(cables.total), x: PAGE_W - MARGIN_R, align: 'right' },
  ])

  // Power cabinets
  if (cabinets.length > 0) {
    y = sectionTitle(doc, t('report.powerCabinets'), y)
    resetRowIndex()
    const cCols = [
      { label: t('report.device'), x: MARGIN_L },
      { label: t('report.voltage'), x: MARGIN_L + 60 },
      { label: t('report.maxPower'), x: MARGIN_L + 95 },
      { label: t('report.outlets'), x: MARGIN_L + 130, align: 'center' as const },
      { label: t('report.quantity'), x: PAGE_W - MARGIN_R, align: 'right' as const },
    ]
    y = tableHeader(doc, cCols, y)
    for (const cab of cabinets) {
      y = tableRow(doc, [
        { text: `${cab.brand} ${cab.name}`, x: MARGIN_L, bold: true },
        { text: cab.voltage, x: MARGIN_L + 60 },
        { text: `${fmtNum(cab.maxPower)} W`, x: MARGIN_L + 95 },
        { text: String(cab.outlets), x: MARGIN_L + 130, align: 'center' },
        { text: String(cab.count), x: PAGE_W - MARGIN_R, align: 'right' },
      ], y)
    }
    y += SEC_GAP
  }

  // Connections by LED wall (clear, deduped, processor → port → panels → redundancy)
  const mireConnMap = buildMireConnMap(dataPages, mires)
  const hasConns = Array.from(mireConnMap.values()).some(m => m.size > 0)

  if (hasConns) {
    y = sectionTitle(doc, t('report.connectionsPerWall'), y)

    for (const mire of mires) {
      const procMap = mireConnMap.get(mire.id)
      if (!procMap || procMap.size === 0) continue

      y = pageBreak(doc, y, 28)

      // Wall header band
      doc.setFillColor(ACCENT_BG.r, ACCENT_BG.g, ACCENT_BG.b)
      doc.rect(MARGIN_L, y - 1, CONTENT_W, 9, 'F')
      doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
      doc.rect(MARGIN_L, y - 1, 3, 9, 'F')
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8.5)
      doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
      doc.text(mire.name, MARGIN_L + 7, y + 5.5)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
      doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
      doc.text(
        `${mire.columns}×${mire.rows}  •  ${mire.totalPixels.width}×${mire.totalPixels.height} px  •  ${mire.totalPanels} panels`,
        MARGIN_L + 60, y + 5.5,
      )
      y += 13

      // Connection table
      resetRowIndex()
      const connCols = [
        { label: t('report.processor'), x: MARGIN_L },
        { label: t('report.port'), x: MARGIN_L + 75 },
        { label: t('report.panelsShort'), x: MARGIN_L + 100, align: 'center' as const },
        { label: t('report.redundancy'), x: PAGE_W - MARGIN_R, align: 'right' as const },
      ]
      y = tableHeader(doc, connCols, y)

      for (const [procId, portMap] of procMap) {
        let proc: LEDProcessor | undefined
        for (const page of dataPages) {
          const el = page.workspaceElements.find(e => e.id === procId)
          if (el) { proc = el.data as LEDProcessor; break }
        }
        const procLabel = proc ? (proc.customName || `${proc.brand} ${proc.model}`) : procId
        const sortedPorts = Array.from(portMap.entries()).sort((a, b) => a[0] - b[0])
        let firstRow = true
        for (const [port, count] of sortedPorts) {
          const redundancy = backupPorts[procId]?.[port]
          y = tableRow(doc, [
            { text: firstRow ? procLabel : '', x: MARGIN_L, bold: firstRow },
            { text: `P${port}`, x: MARGIN_L + 75, bold: true },
            { text: `${count}`, x: MARGIN_L + 100, align: 'center' },
            { text: '', x: PAGE_W - MARGIN_R },
          ], y)
          if (redundancy !== undefined) {
            const badgeTxt = `R > P${redundancy}`
            drawBadge(doc, PAGE_W - MARGIN_R - doc.getTextWidth(badgeTxt) - 5, y - LH + 0.5, badgeTxt, ACCENT)
          }
          firstRow = false
        }
      }
      y += 4
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
// PAGE: POWER SUMMARY (detailed: kW, A, %, circuit capacity, per wall)
// ═══════════════════════════════════════════════════════════════════════
function drawPowerPage(opts: TechnicalPageOpts): void {
  const { doc, t, projectName, panels, processors, mires, cabinets,
    procConsumption, panelConsumption, totalConsumption } = opts

  newTechnicalPage(doc)
  let y = drawPageHeader(doc, t('report.powerSummary'), projectName)

  // ── 3 metric boxes: W / kW / A ──────────────────────────────────────
  const mBoxW = (CONTENT_W - 10) / 3
  const totalKw = totalConsumption / 1000
  const totalAmps = totalConsumption / 230
  drawMetricBox(doc, MARGIN_L, y, mBoxW, 28, t('report.totalEstimatedConsumption'), `${fmtNum(totalConsumption)} W`, true)
  drawMetricBox(doc, MARGIN_L + mBoxW + 5, y, mBoxW, 28, t('report.powerInKw'), `${totalKw.toFixed(2)} kW`, false)
  drawMetricBox(doc, MARGIN_L + 2 * (mBoxW + 5), y, mBoxW, 28, t('report.estimatedCurrent'), `${totalAmps.toFixed(1)} A`, false)
  y += 36

  // ── Proportional bar with % labels ──────────────────────────────────
  const barH = 12
  const panelPct = totalConsumption > 0 ? panelConsumption / totalConsumption : 0.9
  const panelBarW = CONTENT_W * panelPct
  const procBarW = CONTENT_W - panelBarW
  const panelPctStr = `${Math.round(panelPct * 100)}%`
  const procPctStr = `${Math.round((1 - panelPct) * 100)}%`

  doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
  if (panelBarW > 0) doc.roundedRect(MARGIN_L, y, panelBarW, barH, 2, 2, 'F')
  if (panelBarW > 0 && procBarW > 0) doc.rect(MARGIN_L + panelBarW - 2, y, 2, barH, 'F')

  doc.setFillColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  if (procBarW > 0) doc.roundedRect(MARGIN_L + panelBarW, y, procBarW, barH, 2, 2, 'F')
  if (panelBarW > 0 && procBarW > 0) doc.rect(MARGIN_L + panelBarW, y, 2, barH, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  if (panelBarW > 35) {
    doc.setTextColor(255, 255, 255)
    doc.text(`${t('report.panels')} ${panelPctStr}`, MARGIN_L + 5, y + barH / 2 + 2)
  }
  if (procBarW > 35) {
    doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
    doc.text(`${t('report.processors')} ${procPctStr}`, MARGIN_L + panelBarW + 5, y + barH / 2 + 2)
  }
  y += barH + 10

  // ── Two-column breakdown: panels (left) / processors (right) ────────
  const halfW = (CONTENT_W - 10) / 2
  const panelCardH = 16 + panels.length * 7
  const procCardH = 16 + processors.length * 7

  // LEFT: panels card
  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.setLineWidth(0.3)
  doc.roundedRect(MARGIN_L, y, halfW, panelCardH, 2, 2, 'FD')
  doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
  doc.roundedRect(MARGIN_L, y, halfW, 9, 2, 2, 'F')
  doc.rect(MARGIN_L, y + 5, halfW, 4, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  doc.setTextColor(255, 255, 255)
  doc.text(t('report.panelsConsumption'), MARGIN_L + 4, y + 6.5)
  doc.text(`${fmtNum(panelConsumption)} W`, MARGIN_L + halfW - 4, y + 6.5, { align: 'right' })

  let iy = y + 15
  for (const p of panels) {
    const pw = p.powerConsumption * p.count
    const pct = totalConsumption > 0 ? Math.round((pw / totalConsumption) * 100) : 0
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
    doc.text(`${p.brand} ${p.name} (×${p.count})`, MARGIN_L + 4, iy)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(6.5)
    doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
    doc.text(`${fmtNum(pw)} W`, MARGIN_L + halfW - 14, iy, { align: 'right' })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6)
    doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
    doc.text(`${pct}%`, MARGIN_L + halfW - 2, iy, { align: 'right' })
    iy += 7
  }

  // RIGHT: processors card
  const rx = MARGIN_L + halfW + 10
  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.roundedRect(rx, y, halfW, procCardH, 2, 2, 'FD')
  doc.setFillColor(GRAY_100.r, GRAY_100.g, GRAY_100.b)
  doc.roundedRect(rx, y, halfW, 9, 2, 2, 'F')
  doc.rect(rx, y + 5, halfW, 4, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
  doc.text(t('report.processorsConsumption'), rx + 4, y + 6.5)
  doc.text(`${fmtNum(procConsumption)} W`, rx + halfW - 4, y + 6.5, { align: 'right' })

  iy = y + 15
  for (const proc of processors) {
    const pw = typeof proc.powerConsumption === 'number' ? proc.powerConsumption : 0
    const pct = totalConsumption > 0 ? Math.round((pw / totalConsumption) * 100) : 0
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
    doc.text(proc.customName || `${proc.brand} ${proc.model}`, rx + 4, iy)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(6.5)
    doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
    doc.text(pw > 0 ? `${fmtNum(pw)} W` : '—', rx + halfW - 14, iy, { align: 'right' })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6)
    doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
    doc.text(pw > 0 ? `${pct}%` : '', rx + halfW - 2, iy, { align: 'right' })
    iy += 7
  }

  y += Math.max(panelCardH, procCardH) + 12

  // ── Circuit capacity check ───────────────────────────────────────────
  if (cabinets.length > 0) {
    const totalCapacity = cabinets.reduce((s, c) => s + c.maxPower * c.count, 0)
    const loadPct = totalCapacity > 0 ? Math.round((totalConsumption / totalCapacity) * 100) : 0
    const loadColor = loadPct > 90 ? RED : loadPct > 70 ? ORANGE : GREEN

    y = pageBreak(doc, y, 36)
    y = sectionTitle(doc, t('report.circuitCapacity'), y)

    const cBoxW = (CONTENT_W - 10) / 3
    drawMetricBox(doc, MARGIN_L, y, cBoxW, 26, t('report.totalCircuitCapacity'), `${fmtNum(totalCapacity)} W`, false)
    drawMetricBox(doc, MARGIN_L + cBoxW + 5, y, cBoxW, 26, t('report.totalLoad'), `${fmtNum(totalConsumption)} W`, false)

    // Color-coded load % box
    const cx3 = MARGIN_L + 2 * (cBoxW + 5)
    doc.setFillColor(loadColor.r, loadColor.g, loadColor.b)
    doc.roundedRect(cx3, y, cBoxW, 26, 2, 2, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(16)
    doc.text(`${loadPct}%`, cx3 + cBoxW / 2, y + 13, { align: 'center' })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.text(t('report.circuitLoad'), cx3 + cBoxW / 2, y + 21, { align: 'center' })
    y += 34

    // Cabinet detail table
    resetRowIndex()
    const cCols = [
      { label: t('report.device'), x: MARGIN_L },
      { label: t('report.voltage'), x: MARGIN_L + 65 },
      { label: t('report.maxPower'), x: MARGIN_L + 100 },
      { label: t('report.outlets'), x: MARGIN_L + 130, align: 'center' as const },
      { label: t('report.quantity'), x: PAGE_W - MARGIN_R - 20, align: 'center' as const },
      { label: t('report.totalCapacity'), x: PAGE_W - MARGIN_R, align: 'right' as const },
    ]
    y = tableHeader(doc, cCols, y)
    for (const cab of cabinets) {
      y = tableRow(doc, [
        { text: `${cab.brand} ${cab.name}`, x: MARGIN_L, bold: true },
        { text: cab.voltage, x: MARGIN_L + 65 },
        { text: `${fmtNum(cab.maxPower)} W`, x: MARGIN_L + 100 },
        { text: String(cab.outlets), x: MARGIN_L + 130, align: 'center' },
        { text: String(cab.count), x: PAGE_W - MARGIN_R - 20, align: 'center' },
        { text: `${fmtNum(cab.maxPower * cab.count)} W`, x: PAGE_W - MARGIN_R, align: 'right' },
      ], y)
    }
    y = totalRow(doc, y, [
      { text: t('report.total'), x: MARGIN_L },
      { text: `${fmtNum(totalCapacity)} W`, x: PAGE_W - MARGIN_R, align: 'right' },
    ])
  }

  // ── Per-wall power breakdown table (W / kW / A per wall) ────────────
  if (mires.some(m => m.linkedPanel)) {
    y = pageBreak(doc, y, 24)
    y = sectionTitle(doc, t('report.powerPerWall'), y)
    resetRowIndex()
    const pwCols = [
      { label: t('report.ledWall'), x: MARGIN_L },
      { label: t('report.panels'), x: MARGIN_L + 48, align: 'center' as const },
      { label: t('report.perPanel'), x: MARGIN_L + 76, align: 'right' as const },
      { label: t('report.wallTotal'), x: MARGIN_L + 112, align: 'right' as const },
      { label: 'kW', x: MARGIN_L + 135, align: 'right' as const },
      { label: 'A (230V)', x: PAGE_W - MARGIN_R - 5, align: 'right' as const },
      { label: '%', x: PAGE_W - MARGIN_R, align: 'right' as const },
    ]
    y = tableHeader(doc, pwCols, y)

    let wallPowerTotal = 0
    for (const m of mires) {
      if (!m.linkedPanel) continue
      const wallPower = m.linkedPanel.powerConsumption * m.totalPanels
      wallPowerTotal += wallPower
      const wallPct = totalConsumption > 0 ? Math.round((wallPower / totalConsumption) * 100) : 0
      y = tableRow(doc, [
        { text: m.name, x: MARGIN_L, bold: true },
        { text: String(m.totalPanels), x: MARGIN_L + 48, align: 'center' },
        { text: `${fmtNum(m.linkedPanel.powerConsumption)} W`, x: MARGIN_L + 76, align: 'right' },
        { text: `${fmtNum(wallPower)} W`, x: MARGIN_L + 112, align: 'right' },
        { text: `${(wallPower / 1000).toFixed(2)}`, x: MARGIN_L + 135, align: 'right' },
        { text: `${(wallPower / 230).toFixed(1)} A`, x: PAGE_W - MARGIN_R - 5, align: 'right' },
        { text: `${wallPct}%`, x: PAGE_W - MARGIN_R, align: 'right' },
      ], y)
    }
    y = totalRow(doc, y, [
      { text: t('report.total'), x: MARGIN_L },
      { text: `${fmtNum(wallPowerTotal)} W`, x: MARGIN_L + 112, align: 'right' },
      { text: `${(wallPowerTotal / 1000).toFixed(2)} kW`, x: MARGIN_L + 135, align: 'right' },
      { text: `${(wallPowerTotal / 230).toFixed(1)} A`, x: PAGE_W - MARGIN_R - 5, align: 'right' },
    ])
  }

  // ── Disclaimer ───────────────────────────────────────────────────────
  y = pageBreak(doc, y, 10)
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(6.5)
  doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
  doc.text(`* ${t('report.powerDisclaimer')}`, MARGIN_L, y)
}
